import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {P} from 'UI/components/layout/Text';

/**
 * The props for the `Chip` component.
 * Configures a filter pill with active/inactive state.
 */
interface ComponentProps {
    /** Optional icon rendered before the label. */
    icon?: ReactNode;
    /**
     * Whether the chip is in its active (selected) state.
     * Active chips have a primaryDefault background with white text.
     * Inactive chips have a surfLow background with inkSecondary text.
     */
    isActive?: boolean;
    /** The text label displayed inside the chip. */
    label: string;
    /** Callback fired when the chip is clicked. */
    onClick?(): void;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'Chip'
     */
    testId?: string;
}

/**
 * A pill-shaped filter chip matching the Figma design system.
 * Renders as a toggleable pill with active/inactive visual states.
 * Used for category filtering in tour listings.
 *
 * @param props          The component props.
 * @param props.icon     Optional icon element rendered before the label.
 * @param props.isActive Whether the chip is selected. Defaults to `false`.
 * @param props.label    The text label to display.
 * @param props.onClick  Callback fired on click.
 * @param props.testId   An optional test ID used for automation selectors. Defaults to `'Chip'`.
 * @returns A JSX element rendering the Chip.
 *
 * @example
 * ```tsx
 * <Chip label="Historisch" isActive onClick={handleToggle} />
 * <Chip label="Natur" icon={<ForestIcon />} />
 * ```
 */
const Chip = ({icon, isActive = false, label, onClick, testId = 'Chip'}: ComponentProps) => (
    <Wrapper $isActive={isActive} data-cy={testId} type="button" onClick={onClick}>
        {icon && <IconWrapper $isActive={isActive}>{icon}</IconWrapper>}
        {/* ⚠️ Figma: 12px Medium — using labelMedium (12px/600) with overrides for normal-case + weight 500 */}
        <ChipLabel $isActive={isActive} $size="labelMedium" as="span">{label}</ChipLabel>
    </Wrapper>
);

Chip.displayName = 'Chip';

export {Chip};

// ── Styled ──────────────────────────────────────────────────

interface WrapperProps {
    /** Whether the chip is active. */
    $isActive: boolean;
}

const Wrapper = styled.button<WrapperProps>`
    align-items: center;
    background: ${({$isActive, theme}) => ($isActive ? theme.colors.primaryDefault : theme.colors.surfaceLow)};
    border: ${({$isActive, theme}) => ($isActive ? 'none' : `1px solid ${theme.colors.inkDisabled}`)};
    border-radius: 9999px;
    cursor: pointer;
    display: inline-flex;
    flex-direction: row;
    gap: ${spacing(2)};
    height: auto;
    justify-content: center;
    margin: 0;
    padding:  ${spacing(2)} ${spacing(3)};
    transition: background 0.2s ease;
    white-space: nowrap;

    &:hover {
        opacity: 0.85;
    }
`;

interface IconWrapperProps {
    /** Whether the parent chip is active (controls icon color). */
    $isActive: boolean;
}

const IconWrapper = styled.span<IconWrapperProps>`
    align-items: center;
    color: ${({$isActive, theme}) => ($isActive ? theme.colors.white : theme.colors.inkSecondary)};
    display: inline-flex;
    font-size: 1.4rem;

    svg {
        height: 1.4rem;
        width: 1.4rem;
    }
`;

interface ChipLabelProps {
    /** Whether the parent chip is active (controls text color). */
    $isActive: boolean;
}

const ChipLabel = styled(P)<ChipLabelProps>`
    color: ${({$isActive, theme}) => ($isActive ? theme.colors.white : theme.colors.inkSecondary)};
    font-weight: 500;
    letter-spacing: normal;
    text-transform: none;
`;